import {
  CalendarOutlined,
  ClockCircleOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons"
import { FluidObject } from "gatsby-image"
import React from "react"
import readingTime from "reading-time/lib/reading-time"
import styled, { css } from "styled-components"
import ArticleType from "../atoms/article-type"
import BreakpointImage from "../atoms/breakpoint-image"
import Divider from "../atoms/divider"
import Block from "../atoms/layout-primitives/block"
import Spacer from "../atoms/layout-primitives/spacer"
import SmartLink from "../atoms/link"
import Text from "../atoms/text"
import VariantContainer from "../atoms/variant-container"
import { BT } from "../styles/blog-typography"
import { IContentfulTag } from "../types/blog.types"
import { truncateContent } from "../util/contentful-data"

interface IArticleCardProps {
  article: IArticle
}

export interface IArticle {
  image?: FluidObject
  title: string
  date?: string
  readTime?: string
  content?: string
  category: IContentfulTag // required for full url slug
  topic: IContentfulTag // required for full url slug
  type?: string //article, case study, dev content, etc
  slug: string
}

const CardLink = styled(SmartLink)`
  text-decoration: none;
  width: 100%;
  flex-grow: 1;
`

const TopicLink = styled(SmartLink)`
  text-decoration: none;
`

const ArticleCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background-color: var(--white);
  width: auto;
  height: inherit;
  pointer-events: auto;
`

const InfoItem = styled.span`
  ${BT.sm.info`400`}
  opacity: 0.45;

  span {
    margin-right: 8px;
  }
  :not(:last-child) {
    margin-right: 16px;
  }
`

/**
 * Gets the component for an article's media type
 * @param type - article, case study, dev content, etc
 * @returns
 */
const getArticleType = (type: string) => {
  switch (type.toLowerCase()) {
    case "video":
      return <ArticleType.Video />
    case "whitepaper":
      return <ArticleType.Whitepaper />
    case "case study":
      return <ArticleType.CaseStudy />
    case "e-book":
      return <ArticleType.Ebook />
    case "dev content":
      return <ArticleType.DevContent />
    case "article":
    default:
      return <ArticleType.Article />
  }
}

/**
 * Displays an article preview in a small card
 * @param {IArticle} article - The article to display
 */
const ArticleCard = ({ article }: IArticleCardProps) => {
  const { image, title, date, content, category, topic, slug, type } = article
  const readTime = readingTime(content ?? "").text

  return (
    <ArticleCardContainer>
      <CardLink
        to={`/blog/${category.slug}/${topic.slug}/${slug}/`}
        target={"_blank"}
        rel={"noreferrer"}
      >
        {image && (
          <Block
            variants={[
              css`
                * {
                  border-radius: 4px 4px 0 0;
                  max-height: 141px;
                  width: 100%;
                }
              `,
              css`
                * {
                  max-height: 184px;
                }
              `,
              css`
                * {
                  max-height: 208px;
                }
              `,
            ]}
          >
            <BreakpointImage fluid={image} imgStyle={{ objectFit: "cover" }} />
          </Block>
        )}
        <VariantContainer
          variants={[
            css`
              display: flex;
              flex-direction: column;
              padding: 24px;
              max-width: 100%;
            `,
          ]}
        >
          {title && (
            <Text
              as="h3"
              variants={[
                css`
                  ${BT.sm.h3`500`};
                `,
              ]}
            >
              {title}
            </Text>
          )}
          <Spacer height={{ sm: "12px" }} />
          <Block>
            {date && (
              <InfoItem>
                <CalendarOutlined />
                {date}
              </InfoItem>
            )}
            {readTime && (
              <InfoItem>
                <ClockCircleOutlined />
                {readTime}
              </InfoItem>
            )}
          </Block>
          {content && (
            <>
              <Spacer height={{ sm: "12px" }} />
              <Text
                as="p"
                variants={[
                  css`
                    ${BT.sm.body`400`}
                  `,
                ]}
              >
                {truncateContent(content)}
              </Text>
            </>
          )}
        </VariantContainer>
      </CardLink>
      <Block>
        <Divider />
        <Block
          variants={[
            css`
              padding: 16px;
            `,
          ]}
        >
          <VariantContainer
            variants={[
              css`
                display: flex;
                justify-content: space-between;
                span {
                  margin-right: 8px;
                }
              `,
            ]}
          >
            <TopicLink
              to={`/blog/${category.slug}/${topic.slug}/`}
              target={"_blank"}
              rel={"noreferrer"}
            >
              {/* Developers articles have topics hidden for now */}
              {category.slug !== "developers" && (
                <Text
                  as="p"
                  variants={[
                    css`
                      ${BT.sm.info`500`};
                    `,
                  ]}
                >
                  <FolderOpenOutlined />
                  {topic.name}
                </Text>
              )}
            </TopicLink>

            {getArticleType(type ?? "")}
          </VariantContainer>
        </Block>
      </Block>
    </ArticleCardContainer>
  )
}

export default ArticleCard
